import { SettingsIcon } from '@gocanvas/gcds/icons'
import { SettingsLabel } from 'apps/studio/components/shared/inputs/SettingsLabel'
import { useSelector } from 'react-redux'
import { hipaaCompliantSelector } from 'apps/builder/store/selectors'
import { WhiteLabelCheck } from 'apps/builder/components/shared'
import UpgradeIcon from 'apps/studio/assets/icons/Upgrade.svg'
import {
  validateFieldMask,
} from 'apps/builder/lib/input-validation'
import { allEntryTypesSelector } from 'apps/builder/store/selectors'

const validateMaxLength = length => {
  if (length) {
    const convertedValue = String(length).replace(/[^0-9]/g, '')
    if (convertedValue !== length) {
      return 'Max Characters is invalid'
    }
    else if (Number(convertedValue) > 255) {
      return 'Max Characters must be less than 255'
    }
  }
}

export const MaxLength = {
    label: 'Max Characters',
    placeholder: ' ',
    validation: length => ({ errors: validateMaxLength(length) }),
    settingsType: 'textInput',
    property: 'length',
    additionalProps: { maxLength: 3 },
    inputSmall: true,
}
  
export const FieldMask = {
    label: 'Field Mask',
    placeholder: ' ',
    additionalProps: { maxLength: 30 },
    tooltipContent: <>
        {`Field Mask can only contain the following characters: 0, 9, A, a, L, l, ?, *. `}
        <WhiteLabelCheck>
        <div>
            <a
            href="https://help.gocanvas.com/hc/en-us/articles/25139091288087-Using-the-Field-Mask-Setting-in-the-Builder"
            target="blank"
            >
            Learn More
            </a>
        </div>
        </WhiteLabelCheck>
    </>,
    validation: mask => {
        return {
            errors: validateFieldMask(mask),
        }
    }, 
    settingsType: 'numberInput',
    property: 'mask',
}

export const imageSizeChoices = [
    {
        label: 'Smallest',
        value: -1,
    },
    {
        label: 'Thumbnail',
        value: 0,
    },
    {
        label: 'Small',
        value: 2,
    },
    {
        label: 'Medium',
        value: 3,
    },
    {
        label: 'Largest',
        value: 1,
    }
]

export const DecimalPlaces = {
    label: 'Decimal Places',
    settingType: 'selectInput',
    property: 'mask',
    choices: [
        { value: null, label: 'No Limit' },
        { value: '0', label: 'None' },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' },
    ],
}

export const NumberDecimalPlaces = {
    label: 'Decimal Places',
    settingType: 'selectInput',
    property: 'mask',
    dynamicChoices: () => {
      const entryTypes = useSelector(allEntryTypesSelector)

      const { id: integerTypeId } =
        entryTypes.find(({ original_type_id }) => original_type_id === 1)
      const { id: decimalTypeId } =
        entryTypes.find(({ original_type_id }) => original_type_id === 2)

      const decimalEntryType = {
        entry_type_id: decimalTypeId,
        original_type_id: 2,
      }

      const integerEntryType = {
        entry_type_id: integerTypeId,
        original_type_id: 1,
      }

      return [
        {
          value: null, label: 'No Limit',
          additionalProperties: decimalEntryType
        },
        {
          value: '0', label: 'None',
          additionalProperties: integerEntryType
        },
        {
          value: '1', label: '1',
          additionalProperties: decimalEntryType
        },
        {
          value: '2', label: '2',
          additionalProperties: decimalEntryType
        },
        {
          value: '3', label: '3',
          additionalProperties: decimalEntryType
        },
        {
          value: '4', label: '4',
          additionalProperties: decimalEntryType
        },
        {
          value: '5', label: '5',
          additionalProperties: decimalEntryType
        },
        {
          value: '6', label: '6',
          additionalProperties: decimalEntryType
        },
      ]
    },
}

export const DisabledDecimalPlaces = {
    ...DecimalPlaces,
    isDisabled: () => true,
}

export const PDFVisibilitySelect = {
  label: 'On PDF',
  settingType: 'selectInput',
  property: 'pdf_visibility',
  labelAbove: true,
  choices: [
    'Always', // 0
    'Never', // 1
    'Only if field is not empty', // 2
    'Only if changed', // 3
    'Only if changed & not empty', // 4
    'Only to addresses on the Email Options Page', // 5
  ].map((label, value) => ({ label, value })),
}

// TODO: confirm values
export const WebVisibilitySelect =   {
  label: 'In Submission',
  settingType: 'selectInput',
  property: 'web_visibility',
  labelAbove: true,
  choices: [
    'Always',// 0
    'Never', // 1
    'Only if field is not empty', // 2
    'Only if changed', // 3
    'Only if changed & not empty', // 4
  ].map((label, value) => ({ label, value })),
}

export const BothVisibilitySelects = [
  SettingsLabel('Display Value When'),
  PDFVisibilitySelect,
  WebVisibilitySelect,
]

export const EmailBodyVisibilitySelect =   {
  label: 'Email Body',
  labelLarge: true,
  settingType: 'toggleInput',
  property: 'auto_email_body',
}

export const PlaceValueCheckboxes = [
  SettingsLabel('Place Value In'),
  {
    label: 'Email Subject',
    labelLarge: true,
    settingType: 'toggleInput',
    property: 'auto_email_subject',
  },
  EmailBodyVisibilitySelect,
  {
    label: 'PDF File Name',
    labelLarge: true,
    settingType: 'toggleInput',
    property: 'auto_email_filename',
  },
  {
    label: 'Submission Name',
    labelLarge: true,
    settingType: 'toggleInput',
    property: 'auto_submission_name',
  }
]

export const Labels = [
  SettingsLabel('Labels'),
  {
    label: 'Export',
    settingType: 'textInput',
    labelAbove: true,
    property: 'export_label',
  },
  {
    label: 'Receipt',
    settingType: 'textInput',
    labelAbove: true,
    property: 'receipt_label',
  },
  {
    label: 'Submission',
    settingType: 'longTextInput',
    labelAbove: true,
    property: 'report_label',
  },
]

// if the hipaaCompliantSelector is true, this cannot be changed, and should not be shown
export const MakeAllowUnsecuredAccessToggle = mediaType => ({
  label: 'Allow Unsecured Access',
  tooltipContent: <>
    {`Checking this box will make ${mediaType} uploaded to this field accessible via URL even if you are not logged in.`}
    <WhiteLabelCheck>
      <div>
        <a
          href="https://help.gocanvas.com/hc/en-us/articles/23404128090391-What-is-the-Unsecured-Access-setting-in-the-Builder"
          target="blank"
        >
          Learn More
        </a>
      </div>
    </WhiteLabelCheck>
  </>,
  settingType: 'toggleInput',
  property: 'allow_unsecured_access',
  isDisabled: () => useSelector(hipaaCompliantSelector)
})

export const ADVANCED_PANEL = 'Advanced'

const AdvancedSettingsUpsell = () => (
  <div className="advanced-settings--plg-upsell">
    <UpgradeIcon aria-hidden="true" />
    <p className="advanced-settings--plg-upsell--text">
      <strong>Upgrade</strong> to apply advanced settings to your form like
      field validation, character requirements, and more.
    </p>
  </div>
)

export const AdvancedSettingsPanel = (settings = []) => ({
  label: ADVANCED_PANEL,
  icon: SettingsIcon,
  settings,
  plgUpsell: AdvancedSettingsUpsell,
})

// commonly used advanced settings
export const StandardAdvancedSettings = [
    ...PlaceValueCheckboxes,
    ...BothVisibilitySelects,
    ...Labels,
]

export const LimitedAdvancedSettings = [
    ...BothVisibilitySelects,
    ...Labels,
]

// base advanced settings
export const AdvancedPanel = AdvancedSettingsPanel(StandardAdvancedSettings)
export const LimitedAdvancedPanel = AdvancedSettingsPanel(LimitedAdvancedSettings)
